import { useState } from 'react';
import { T, useGT } from 'gt-react';

import Page from '@/components/page/Page';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { XlsxViewer } from '@/components/xlsx-viewer/XlsxViewer';
import { CorporateUploadPanel } from './CorporateUploadPanel';
import { deriveConversion } from './conversion';
import { ConversionReport } from './ConversionReport';
import { DownloadCsvButton } from './DownloadCsvButton';
import { HubbellFeedbackCard } from './hubbell-feedback/HubbellFeedbackCard';
import { LayoutsPanel } from './LayoutsPanel';
import { MassRenewalCard } from './mass-renewal/MassRenewalCard';
import { SupplierDialog } from './SupplierDialog';
import { UploadCard } from './UploadCard';
import { UploadPreview } from './UploadPreview';
import { UploadRulesPanel } from './UploadRulesPanel';
import { UnloadedSpasCard } from './unloaded-spas/UnloadedSpasCard';
import { useResultsFiles } from './useResultsFiles';
import { useUploadToolData } from './useUploadToolData';

/** Height of the workbook viewer, about 25 rows of 24px; it scrolls inside this. */
const VIEWER_HEIGHT = 620;

/**
 * Pricing → Results → Upload: turn a supplier's results workbook into the
 * AS400 Header Cross CSV the Menu 117 mass load takes, through that supplier's
 * layout, checked against the org's validation rules. The conversion runs in
 * the browser; only the layouts and rules are stored.
 */
export function ResultsToUploadPage() {
  const gt = useGT();
  const files = useResultsFiles();
  const { workbook, sheet, lookupSheet, pickedLayoutId } = files;
  const data = useUploadToolData();
  const { layouts, rules } = data;
  const [tab, setTab] = useState<'supplier' | 'corporate'>('supplier');

  // Re-derived on every render, so switching layout or editing a rule
  // re-checks the file already on the page without dropping it again.
  const conversion = deriveConversion({
    sheet,
    lookupSheet,
    layouts: data.supplierLayouts,
    pickedLayoutId,
    rules,
  });

  return (
    <Page
      title={gt('Results → Upload', {
        context: 'Pricing Results to Upload page title',
      })}
      subtitle={gt(
        'Convert a supplier results workbook into the AS400 Header Cross upload CSV.',
        { context: 'Pricing Results to Upload page subtitle' },
      )}
      actions={
        tab === 'supplier' && conversion.checked ? (
          <DownloadCsvButton
            csv={conversion.checked.upload.csv}
            summary={conversion.checked.summary}
            rulesReady={data.downloadReady}
          />
        ) : null
      }
    >
      <SupplierDialog
        files={files.pendingFiles}
        layouts={data.supplierLayouts}
        ready={data.layoutsReady}
        onCancel={() => files.setPendingFiles([])}
        onConfirm={files.confirm}
      />
      <Tabs
        value={tab}
        onValueChange={(next) =>
          setTab(next === 'corporate' ? 'corporate' : 'supplier')
        }
      >
        <TabsList>
          <TabsTrigger value="supplier">
            <T context="Results to Upload tab: supplier results workbooks">
              Supplier Results
            </T>
          </TabsTrigger>
          <TabsTrigger value="corporate">
            <T context="Results to Upload tab: Rockwell corporate SPAs">
              Upload Corporate
            </T>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="supplier">
          {/* Half a screen of room below the last sheet, so it can be scrolled up
              to the middle of the screen instead of staying pinned at the bottom. */}
          <div className="flex flex-col gap-3 pt-3 pb-[50vh]">
            {/* Three equal cards: the file, how suppliers map and what gets
                checked; then the Hubbell and ABB Feedback cards at half
                width each, as tall as their Division cards need; then the
                Hubbell and ABB Mass Renewal cards the same way, under their
                Feedback cards; then ABB Unloaded SPAs across the row. */}
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <UploadCard
                fileName={workbook?.fileName ?? null}
                isReading={files.isReading}
                layouts={data.supplierLayouts}
                supplierId={conversion.layout?.id ?? null}
                sheetName={files.sheetName}
                sheetNames={files.resultsSheetNames}
                onFiles={files.setPendingFiles}
                onSupplierChange={files.setPickedLayoutId}
                onSheetChange={files.selectSheet}
              />
              <LayoutsPanel
                layouts={layouts}
                unreadableLayoutIds={data.unreadableLayoutIds}
                sheet={sheet}
                lookupSheet={lookupSheet}
                onSaved={(saved) => files.setPickedLayoutId(saved.id)}
              />
              <UploadRulesPanel
                rules={rules}
                layouts={layouts}
                unreadableRuleIds={data.unreadableRuleIds}
                isLoading={data.rulesLoading}
                isError={data.rulesError}
              />
              <div className="md:col-span-2">
                <HubbellFeedbackCard supplier="hubbell" />
              </div>
              <div className="md:col-span-2">
                <HubbellFeedbackCard supplier="abb" />
              </div>
              <div className="md:col-span-2">
                <MassRenewalCard supplier="hubbell" />
              </div>
              <div className="md:col-span-2">
                <MassRenewalCard supplier="abb" />
              </div>
              <div className="md:col-span-2 xl:col-span-4">
                <UnloadedSpasCard />
              </div>
            </div>

            <ConversionReport
              fileName={workbook?.fileName ?? null}
              headers={sheet?.headers ?? []}
              readError={files.readError}
              conversion={conversion}
            />

            {workbook ? (
              <XlsxViewer
                url={workbook.fileUrl}
                fileName={workbook.fileName}
                height={VIEWER_HEIGHT}
                // One sheet is both viewed and converted: a tab picked here
                // converts it, as the sheet picker does.
                sheetName={files.sheetName ?? undefined}
                onSheetChange={(name) => {
                  if (files.resultsSheetNames.includes(name)) {
                    files.selectSheet(name);
                  }
                }}
              />
            ) : null}

            {conversion.checked ? (
              <UploadPreview lines={conversion.checked.upload.lines} />
            ) : null}
          </div>
        </TabsContent>
        {/* Kept mounted: its dropped files and picks survive a tab switch. */}
        <TabsContent
          value="corporate"
          forceMount
          className={tab === 'corporate' ? undefined : 'hidden'}
        >
          <CorporateUploadPanel />
        </TabsContent>
      </Tabs>
    </Page>
  );
}
